import { useEffect, useId, useMemo, useState } from 'react';
import { dateTime } from '@orgazone/date-format';
import { api, msgTypeLabel, type ApiMapping, type ApiMappingDetail, type ApiMessage, type ApiSupplier, type MappingValidateResult } from '../api';
import { Icon } from '../components/icons';
import { Modal } from '../components/Modal';
import { Badge, Btn, Card, DataGate, Dot, PageHead, Spinner } from '../components/ui';
import { useApp } from '../state';
import { useResource } from '../useResource';

// Mappings (§6) — des objets PARTAGÉS : un mapping traduit un flux (factures XML, catalogue positionnel…) et se
// rattache à autant de fournisseurs qu'il en faut ; une nouvelle version s'applique à tous à la fois, et la
// précédente reste disponible pour revenir en arrière. L'écran montre deux choses : la COUVERTURE (chaque
// fournisseur × type déclaré : en place / manquant / à revoir — « à revoir » = un vrai message de ce type est en
// erreur, rien d'inventé §23.0) et les MAPPINGS eux-mêmes, avec leurs fournisseurs.
// ⚠️ Un fournisseur ne reçoit un même flux que par UN mapping : le serveur refuse le second en nommant le premier,
//    et l'écran affiche ce refus tel quel plutôt que de deviner.

type CovStatus = 'ok' | 'missing' | 'review';
interface CovRow { key: string; supplierId: string; supplierName: string; type: string; mappings: ApiMapping[]; status: CovStatus }

const selSt = { height: 30, borderRadius: 'var(--r-xs)', border: '1px solid var(--ink-200)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-body)', padding: '0 7px', cursor: 'pointer' } as const;
const lbl = { fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--ink-600)', marginBottom: 5, display: 'block' } as const;
const inp = { height: 36, borderRadius: 'var(--r-sm)', border: '1px solid var(--ink-200)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-lead)', padding: '0 11px', width: '100%' } as const;
const ta = { width: '100%', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink-200)', background: 'var(--ink-50)', color: 'var(--ink-800)', fontSize: 'var(--t-note)', fontFamily: 'var(--font-mono)', padding: 10 } as const;
const section = { fontSize: 'var(--t-sub)', fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 6 } as const;
const linkBtn = { background: 'none', border: 'none', padding: 0, fontSize: 'var(--t-note)', color: 'var(--teal-700)', fontWeight: 500, cursor: 'pointer' } as const;

/** Le flux d'un mapping en une ligne : ce qu'il reconnaît et ce qu'il produit. */
function flowLabel(m: { sourceFormat: string; discriminant: string; canonicalType: string }): string {
  return `${m.sourceFormat} « ${m.discriminant} » → ${m.canonicalType}`;
}

export function Mappings() {
  const { guard } = useApp();
  const [filter, setFilter] = useState<'all' | CovStatus>('all');
  const [supFilter, setSupFilter] = useState('all');
  const [creating, setCreating] = useState<{ supplierIds: string[] } | null>(null);
  const [attaching, setAttaching] = useState<{ supplierId: string; supplierName: string; type: string } | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  // Les trois lectures forment UN seul chargement : la couverture est un CROISEMENT (types déclarés × mappings
  // rattachés × messages en erreur) — affichée sur deux listes arrivées et une manquante, elle annoncerait des
  // manques qui n'existent pas.
  const { state, data, reload, error } = useResource(async (signal) => {
    const [mappings, suppliers, errorMsgs] = await Promise.all([
      api.get<ApiMapping[]>('/admin/mappings', signal),
      api.get<ApiSupplier[]>('/admin/suppliers', signal),
      api.get<ApiMessage[]>('/admin/messages?status=error&limit=200', signal),
    ]);
    return { mappings, suppliers, errorMsgs };
  });
  const suppliers = data?.suppliers ?? [];
  const mappings = data?.mappings ?? [];

  // ⚠️ Mémoïsé sur `data` lui-même, jamais sur des listes dérivées en `?? []` : celles-ci changent d'identité à
  //    chaque rendu, ce qui recalculerait la couverture à chaque fois.
  const rows = useMemo<CovRow[]>(() => {
    if (!data) return [];
    const out: CovRow[] = [];
    for (const s of data.suppliers) {
      for (const type of s.messageTypes) {
        const ms = data.mappings.filter((m) => m.canonicalType === type && m.suppliers.some((x) => x.id === s.id));
        const hasErr = ms.length > 0 && data.errorMsgs.some((e) => e.supplierName === s.name && e.canonicalType === type);
        out.push({ key: `${s.id}|${type}`, supplierId: s.id, supplierName: s.name, type, mappings: ms, status: ms.length === 0 ? 'missing' : hasErr ? 'review' : 'ok' });
      }
    }
    return out;
  }, [data]);

  const counts = useMemo(() => ({
    ok: rows.filter((r) => r.status === 'ok').length,
    missing: rows.filter((r) => r.status === 'missing').length,
    review: rows.filter((r) => r.status === 'review').length,
    total: rows.length,
  }), [rows]);

  const shown = rows.filter((r) => (filter === 'all' || r.status === filter) && (supFilter === 'all' || r.supplierId === supFilter));

  const pageHead = (
    <PageHead title="Mappings"
      sub="Un mapping traduit un flux d'un fournisseur. Il peut servir à plusieurs fournisseurs : une nouvelle version s'applique à tous à la fois, et l'ancienne reste disponible pour revenir en arrière."
      actions={<Btn icon="plus" onClick={() => { if (guard()) setCreating({ supplierIds: [] }); }}>Nouveau mapping</Btn>} />
  );

  return (
    <DataGate state={state} onRetry={reload} error={error} head={pageHead}>

      {/* 3 cartes-stats filtrantes */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 14 }}>
        <StatCard on={filter === 'ok'} tone="ok" n={counts.ok} label="Mappings en place" sub={`sur ${counts.total} type${counts.total > 1 ? 's' : ''} déclaré${counts.total > 1 ? 's' : ''}`} onClick={() => setFilter(filter === 'ok' ? 'all' : 'ok')} />
        <StatCard on={filter === 'missing'} tone="rose" n={counts.missing} label="Manquants" sub={counts.missing ? 'indisponibles pour les clients' : 'aucun manque'} onClick={() => setFilter(filter === 'missing' ? 'all' : 'missing')} />
        <StatCard on={filter === 'review'} tone="amber" n={counts.review} label="À revoir" sub="en place mais message en erreur" onClick={() => setFilter(filter === 'review' ? 'all' : 'review')} />
      </div>

      {/* Couverture */}
      <Card style={{ marginBottom: 14 }}>
        <div style={{ padding: '13px 16px', borderBottom: '1px solid var(--ink-200)', display: 'flex', alignItems: 'center', gap: 11, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--t-title)', fontWeight: 600, flex: 1, color: 'var(--ink-800)' }}>Par fournisseur et type de message</span>
          <select aria-label="Fournisseur" value={supFilter} onChange={(e) => setSupFilter(e.target.value)} style={selSt}>
            <option value="all">Tous les fournisseurs</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        {filter !== 'all' && (
          <div style={{ padding: '8px 16px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-100)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-600)' }}>Filtre : {filter === 'ok' ? 'en place' : filter === 'missing' ? 'manquants' : 'à revoir'}</span>
            <button type="button" onClick={() => setFilter('all')} style={linkBtn}>Tout afficher</button>
          </div>
        )}
        {shown.length === 0 && (
          <div style={{ padding: 16, fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>
            {rows.length === 0 ? "Aucun type déclaré — déclarez les « types gérés » d'un fournisseur (sa fiche) pour voir la couverture." : 'Aucune ligne ne correspond à ce filtre.'}
          </div>
        )}
        {shown.map((r, i) => (
          <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 16px', borderBottom: i < shown.length - 1 ? '1px solid var(--ink-100)' : 'none', flexWrap: 'wrap' }}>
            <Dot kind={r.status === 'ok' ? 'ok' : r.status === 'review' ? 'warn' : 'err'} />
            <div style={{ width: 150, flexShrink: 0, minWidth: 0 }}><div style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-800)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.supplierName}</div></div>
            <div style={{ width: 160, flexShrink: 0 }}><span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink-800)' }}>{r.type}</span><div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{msgTypeLabel(r.type)}</div></div>
            <div style={{ flex: 1, minWidth: 140, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {r.mappings.length === 0 && <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>—</span>}
              {r.mappings.map((m) => (
                <button key={m.id} type="button" onClick={() => setOpenId(m.id)} aria-label={`Ouvrir le mapping ${m.name} (${r.supplierName}, ${r.type})`}
                  style={{ ...linkBtn, textAlign: 'left', fontSize: 'var(--t-note)' }}>
                  {m.name} <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-500)' }}>· v{m.currentVersion}</span>
                </button>
              ))}
            </div>
            <Badge kind={r.status === 'ok' ? 'ok' : r.status === 'review' ? 'warn' : 'err'}>{r.status === 'ok' ? 'en place' : r.status === 'review' ? 'à revoir' : 'manquant'}</Badge>
            {r.status === 'missing' && (
              <Btn size="sm" icon="plus" onClick={() => { if (guard()) setAttaching({ supplierId: r.supplierId, supplierName: r.supplierName, type: r.type }); }}>
                <span aria-label={`Rattacher un mapping ${r.type} à ${r.supplierName}`}>Rattacher</span>
              </Btn>
            )}
          </div>
        ))}
      </Card>

      {/* Les mappings eux-mêmes */}
      <Card>
        <div style={{ padding: '13px 16px', borderBottom: '1px solid var(--ink-200)' }}>
          <span style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-800)' }}>Tous les mappings ({mappings.length})</span>
        </div>
        {mappings.length === 0 && <div style={{ padding: 16, fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>Aucun mapping — créez-en un avec « Nouveau mapping ».</div>}
        {mappings.map((m, i) => (
          <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 16px', borderBottom: i < mappings.length - 1 ? '1px solid var(--ink-100)' : 'none', flexWrap: 'wrap' }}>
            <Dot kind={m.suppliers.length ? 'ok' : 'off'} />
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-800)' }}>{m.name}</div>
              <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>{flowLabel(m)} · v{m.currentVersion} en service sur {m.versionCount}</div>
            </div>
            <div style={{ flex: 1, minWidth: 160, fontSize: 'var(--t-note)', color: m.suppliers.length ? 'var(--ink-700)' : 'var(--amber-fg)' }}>
              {m.suppliers.length ? m.suppliers.map((s) => s.name).join(', ') : 'aucun fournisseur — ne traduit rien'}
            </div>
            <Btn size="sm" variant="ghost" mutating={false} onClick={() => setOpenId(m.id)}>
              <span aria-label={`Ouvrir le mapping ${m.name}`}>Ouvrir</span>
            </Btn>
          </div>
        ))}
      </Card>

      {creating && <CreateModal suppliers={suppliers} initialSupplierIds={creating.supplierIds} onClose={() => setCreating(null)} onDone={async () => { setCreating(null); await reload(); }} />}
      {attaching && (
        <AttachModal target={attaching} mappings={mappings} onClose={() => setAttaching(null)}
          onCreate={() => { const t = attaching; setAttaching(null); setCreating({ supplierIds: [t.supplierId] }); }}
          onDone={async () => { setAttaching(null); await reload(); }} />
      )}
      {openId && <MappingModal id={openId} suppliers={suppliers} onClose={() => setOpenId(null)} onChanged={reload} />}
    </DataGate>
  );
}

// Carte-stat filtrante (en place / manquants / à revoir) — sélectionnée = bord + fond teintés.
function StatCard({ on, tone, n, label, sub, onClick }: { on: boolean; tone: 'ok' | 'rose' | 'amber'; n: number; label: string; sub: string; onClick: () => void }) {
  const fg = tone === 'ok' ? 'var(--ok-fg)' : tone === 'rose' ? 'var(--rose-fg)' : 'var(--amber-fg)';
  const bg = tone === 'ok' ? 'var(--ok-bg)' : tone === 'rose' ? 'var(--rose-bg)' : 'var(--amber-bg)';
  return (
    <button type="button" onClick={onClick}
      style={{ textAlign: 'left', padding: '13px 15px', borderRadius: 'var(--r-sm)', border: `1px solid ${on ? fg : 'var(--ink-200)'}`, background: on ? bg : 'var(--paper)', boxShadow: on ? `0 0 0 3px ${bg}` : 'none', cursor: 'pointer' }}>
      <div style={{ fontSize: 'var(--t-sub)', fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase', color: tone === 'rose' ? 'var(--rose-fg)' : 'var(--ink-500)' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.035em', fontVariantNumeric: 'tabular-nums', color: fg, margin: '2px 0' }}>{n}</div>
      <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{sub}</div>
    </button>
  );
}

// Zone de dépôt d'un fichier (lit son texte) : on charge le fichier de l'intégrateur plutôt que de l'écrire à la main.
function FileDrop({ title, hint, accept, loaded, onLoad }: { title: string; hint: string; accept: string; loaded: string | null; onLoad: (text: string, name: string) => void }) {
  return (
    <label style={{ display: 'block', border: `2px dashed ${loaded ? 'var(--teal-500)' : 'var(--ink-300)'}`, borderRadius: 'var(--r-sm)', padding: 16, textAlign: 'center', cursor: 'pointer', background: loaded ? 'var(--teal-50)' : 'var(--paper)' }}>
      <input type="file" accept={accept} style={{ display: 'none' }}
        onChange={async (e) => { const f = e.target.files?.[0]; if (f) onLoad(await f.text(), f.name); }} />
      <div style={{ width: 34, height: 34, borderRadius: 'var(--r-sm)', background: 'var(--ink-100)', color: 'var(--ink-500)', display: 'grid', placeItems: 'center', margin: '0 auto 8px' }}><Icon name="upload" size={17} /></div>
      <div style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-800)' }}>{loaded ? `Chargé : ${loaded}` : title}</div>
      <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 3, lineHeight: 1.45 }}>{hint}</div>
    </label>
  );
}

/**
 * Le choix des règles et leur validation RÉELLE par le moteur — partagé par la création d'un mapping et par
 * l'import d'une nouvelle version. Rend les règles lues et le verdict ; `null` tant que rien n'est validé.
 */
function useRulesPicker() {
  const { showToast } = useApp();
  const [rulesText, setRulesText] = useState('');
  const [rulesName, setRulesName] = useState<string | null>(null);
  const [sample, setSample] = useState('');
  const [sampleName, setSampleName] = useState<string | null>(null);
  const [paste, setPaste] = useState(false);
  const [res, setRes] = useState<MappingValidateResult | null>(null);
  const [busy, setBusy] = useState(false);

  const parseRules = (): unknown | undefined => {
    try { return JSON.parse(rulesText); } catch { showToast('Fichier de mapping invalide (JSON) — vérifiez le fichier', 'err'); return undefined; }
  };
  const validate = async () => {
    const rules = parseRules(); if (rules === undefined) return;
    setBusy(true);
    // ⚠️ Le nom du fichier d'exemple part avec lui : un mapping peut lire le magasin dans le nom (`fileNameGln`).
    try { setRes(await api.post<MappingValidateResult>('/admin/mappings/validate', { rules, sample: sample.trim() || undefined, sampleName: sample.trim() ? sampleName ?? undefined : undefined })); }
    catch (e) { showToast('Validation impossible : ' + (e as Error).message, 'err'); }
    finally { setBusy(false); }
  };
  // ⚠️ Les deux familles de refus comptent : l'écriture est refusée par le serveur dans les DEUX cas, donc un
  //    bouton encore actif enverrait l'opérateur chercher la panne ailleurs (§23.0).
  const valid = !!res?.valid && (res.shapeErrors?.length ?? 0) === 0 && (res.storeRedirections?.length ?? 0) === 0;

  const fields = (
    <>
      <FileDrop title="Déposer le fichier de mapping (.json)" hint="Décrit la traduction format → canonique." accept=".json,application/json"
        loaded={rulesName} onLoad={(t, n) => { setRulesText(t); setRulesName(n); setRes(null); }} />
      <button type="button" onClick={() => setPaste((p) => !p)} style={{ ...linkBtn, justifySelf: 'start' }}>{paste ? 'Masquer' : 'Coller le JSON à la place'}</button>
      {paste && <textarea aria-label="Règles du mapping (JSON)" value={rulesText} onChange={(e) => { setRulesText(e.target.value); setRulesName(null); setRes(null); }} placeholder='{ "supplier": "…", "format": "xml" | "edifact" | "positional", "canonicalType": "…", … }' style={{ ...ta, height: 130 }} />}
      <FileDrop title="Fichier exemple du fournisseur (optionnel)" hint="Pour VALIDER : la console tente une vraie traduction et montre ce que le back-office recevrait." accept="*"
        loaded={sampleName} onLoad={(t, n) => { setSample(t); setSampleName(n); }} />
    </>
  );
  return { fields, res, valid, busy, setBusy, validate, parseRules, hasRules: !!rulesText.trim() };
}

// Le verdict de validation RÉEL : forme, routage, et ce que le back-office recevrait de l'exemple.
function ValidationReport({ res }: { res: MappingValidateResult }) {
  if (res.shapeErrors && res.shapeErrors.length > 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--rose-fg)' }}>Forme du mapping invalide</div>
        {res.shapeErrors.map((e, i) => <div key={i} style={{ display: 'flex', gap: 8, fontSize: 'var(--t-dense)', color: 'var(--ink-700)' }}><Dot kind="err" />{e}</div>)}
      </div>
    );
  }
  if (res.storeRedirections && res.storeRedirections.length > 0) {
    /* ⚠️ Titre PROPRE, jamais « forme invalide » : la forme est correcte, c'est le ROUTAGE qui est faux — annoncer
       une erreur de forme enverrait corriger un champ qui n'a rien. */
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--rose-fg)' }}>Redirection de magasin — refusé</div>
        {res.storeRedirections.map((e, i) => <div key={i} style={{ display: 'flex', gap: 8, fontSize: 'var(--t-dense)', color: 'var(--ink-700)' }}><Dot kind="err" />{e}</div>)}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 'var(--t-body)', color: 'var(--ok-fg)', display: 'flex', alignItems: 'center', gap: 6 }}><Icon name="check" size={14} sw={3} />Forme valide — {res.messageType} → {res.canonicalType}</div>
      {res.report && res.report.recognized === false && (
        <div style={{ padding: '8px 10px', borderRadius: 'var(--r-xs)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: 'var(--t-dense)' }}>Exemple non reconnu par ce mapping : {res.report.reason}</div>
      )}
      {res.report && res.report.recognized === true && (
        <>
          <div style={{ fontSize: 'var(--t-dense)', color: res.report.hasErrors ? 'var(--rose-fg)' : 'var(--ok-fg)' }}>
            Exemple traduit — GLN {res.report.gln ?? '(absent)'} · {res.report.hasErrors ? 'diagnostics bloquants' : 'aucun blocage'}
          </div>
          {res.report.diagnostics.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {res.report.diagnostics.map((d, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, padding: '6px 9px', borderRadius: 'var(--r-xs)', background: d.kind === 'err' ? 'var(--rose-bg)' : 'var(--amber-bg)' }}>
                  <span style={{ marginTop: 2 }}><Dot kind={d.kind} /></span>
                  <div><div style={{ fontSize: 'var(--t-note)', fontWeight: 600, color: d.kind === 'err' ? 'var(--rose-fg)' : 'var(--amber-fg)' }}>{d.title}</div><div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-600)', marginTop: 1 }}>{d.detail}</div></div>
                </div>
              ))}
            </div>
          )}
          <div>
            <div style={section}>Ce que le back-office recevrait</div>
            <pre style={{ margin: 0, padding: 10, background: 'var(--ink-50)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)', fontSize: 'var(--t-meta)', fontFamily: 'var(--font-mono)', color: 'var(--ink-700)', overflowX: 'auto', maxHeight: 200 }}>{JSON.stringify(res.report.canonical, null, 2)}</pre>
          </div>
        </>
      )}
    </div>
  );
}

// Créer un mapping : un nom, ses règles validées, et les fournisseurs qu'il traduit (aucun = il attend d'être rattaché).
function CreateModal({ suppliers, initialSupplierIds, onClose, onDone }: { suppliers: ApiSupplier[]; initialSupplierIds: string[]; onClose: () => void; onDone: () => void }) {
  const fieldId = useId();
  const { showToast, guard } = useApp();
  const picker = useRulesPicker();
  const [name, setName] = useState('');
  const [picked, setPicked] = useState<Set<string>>(() => new Set(initialSupplierIds));

  const toggle = (id: string) => setPicked((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const doCreate = async () => {
    if (!guard()) return;
    const rules = picker.parseRules(); if (rules === undefined) return;
    picker.setBusy(true);
    try {
      await api.post('/admin/mappings', { name, rules, supplierIds: [...picked] });
      showToast(`Mapping « ${name.trim()} » créé`, 'ok');
      onDone();
    } catch (e) { showToast('Refusé : ' + (e as Error).message, 'err'); picker.setBusy(false); }
  };
  const canCreate = picker.valid && !!name.trim();

  return (
    <Modal icon="plus" tone="teal" maxWidth={720} title="Nouveau mapping" sub="Validé sur un exemple avant d'être mis en service" onClose={onClose}
      footer={<>
        <Btn variant="ghost" mutating={false} onClick={onClose}>Annuler</Btn>
        <span style={{ flex: 1 }} />
        <Btn variant="ghost" mutating={false} icon="sparkles" disabled={picker.busy || !picker.hasRules} onClick={() => void picker.validate()}>{picker.busy ? 'Validation…' : 'Valider'}</Btn>
        <Btn icon="check" disabled={picker.busy || !canCreate} onClick={() => void doCreate()}>Créer</Btn>
      </>}>
      <div style={{ display: 'grid', gap: 13 }}>
        <div>
          <label htmlFor={`${fieldId}-name`} style={lbl}>Nom</label>
          <input id={`${fieldId}-name`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Bestseller — factures XML" style={inp} />
        </div>
        <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
          <legend style={lbl}>Fournisseurs qu'il traduit</legend>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {suppliers.length === 0 && <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>Aucun fournisseur — le mapping pourra être rattaché plus tard.</span>}
            {suppliers.map((s) => (
              <label key={s.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-pill)', fontSize: 'var(--t-note)', color: 'var(--ink-700)', cursor: 'pointer' }}>
                <input type="checkbox" checked={picked.has(s.id)} onChange={() => toggle(s.id)} />{s.name}
              </label>
            ))}
          </div>
        </fieldset>
        {picker.fields}
      </div>
      {picker.res && <div style={{ marginTop: 14 }}><ValidationReport res={picker.res} /></div>}
    </Modal>
  );
}

// Nouvelle version d'un mapping : elle s'applique AUSSITÔT à tous ses fournisseurs — l'écran les nomme avant l'import.
function VersionModal({ mapping, onClose, onDone }: { mapping: ApiMappingDetail; onClose: () => void; onDone: () => void }) {
  const { showToast, guard } = useApp();
  const picker = useRulesPicker();
  // Une version qui reconnaît un autre flux ou produit autre chose est refusée par le serveur : l'écran le dit
  // dès la validation, plutôt que d'offrir un bouton qui échouera.
  const otherFlow = picker.res?.valid && (picker.res.messageType !== mapping.discriminant || picker.res.canonicalType !== mapping.canonicalType);
  const doImport = async () => {
    if (!guard()) return;
    const rules = picker.parseRules(); if (rules === undefined) return;
    picker.setBusy(true);
    try {
      const { version } = await api.post<{ version: number }>(`/admin/mappings/${mapping.id}/versions`, { rules });
      showToast(`Version ${version} en service`, 'ok');
      onDone();
    } catch (e) { showToast('Refusé : ' + (e as Error).message, 'err'); picker.setBusy(false); }
  };

  return (
    <Modal icon="upload" tone="teal" maxWidth={720} title="Nouvelle version" sub={mapping.name} onClose={onClose}
      footer={<>
        <Btn variant="ghost" mutating={false} onClick={onClose}>Annuler</Btn>
        <span style={{ flex: 1 }} />
        <Btn variant="ghost" mutating={false} icon="sparkles" disabled={picker.busy || !picker.hasRules} onClick={() => void picker.validate()}>{picker.busy ? 'Validation…' : 'Valider'}</Btn>
        <Btn icon="check" disabled={picker.busy || !picker.valid || !!otherFlow} onClick={() => void doImport()}>Mettre en service</Btn>
      </>}>
      <div style={{ display: 'grid', gap: 13 }}>
        <div style={{ padding: '10px 12px', borderRadius: 'var(--r-sm)', background: mapping.suppliers.length ? 'var(--amber-bg)' : 'var(--ink-50)', color: mapping.suppliers.length ? 'var(--amber-fg)' : 'var(--ink-600)', fontSize: 'var(--t-dense)', lineHeight: 1.5 }}>
          {mapping.suppliers.length
            ? <>Cette version traduira aussitôt pour <b>{mapping.suppliers.map((s) => s.name).join(', ')}</b>. La version {mapping.currentVersion} restera disponible pour revenir en arrière.</>
            : <>Ce mapping n'est rattaché à aucun fournisseur : la version ne traduira rien tant qu'il ne l'est pas.</>}
        </div>
        {picker.fields}
      </div>
      {picker.res && <div style={{ marginTop: 14 }}><ValidationReport res={picker.res} /></div>}
      {otherFlow && (
        <div style={{ marginTop: 12, padding: '8px 11px', borderRadius: 'var(--r-sm)', background: 'var(--rose-bg)', color: 'var(--rose-fg)', fontSize: 'var(--t-note)' }}>
          Ce fichier reconnaît « {picker.res?.messageType} » et produit {picker.res?.canonicalType} ; « {mapping.name} » reconnaît « {mapping.discriminant} » et produit {mapping.canonicalType}. C'est un autre mapping : créez-le avec « Nouveau mapping ».
        </div>
      )}
    </Modal>
  );
}

// Rattacher un mapping existant à un fournisseur pour un type déclaré, ou en créer un.
function AttachModal({ target, mappings, onClose, onCreate, onDone }: {
  target: { supplierId: string; supplierName: string; type: string }; mappings: ApiMapping[]; onClose: () => void; onCreate: () => void; onDone: () => void;
}) {
  const { showToast, guard } = useApp();
  const [busy, setBusy] = useState(false);
  const candidates = mappings.filter((m) => m.canonicalType === target.type && !m.suppliers.some((s) => s.id === target.supplierId));
  const attach = async (m: ApiMapping) => {
    if (!guard()) return;
    setBusy(true);
    try {
      await api.put(`/admin/mappings/${m.id}/suppliers/${target.supplierId}`);
      showToast(`« ${m.name} » traduit désormais pour ${target.supplierName}`, 'ok');
      onDone();
    } catch (e) { showToast('Refusé : ' + (e as Error).message, 'err'); setBusy(false); }
  };
  return (
    <Modal icon="layers" tone="teal" maxWidth={620} title={`Rattacher un mapping ${target.type}`} sub={target.supplierName} onClose={onClose}
      footer={<>
        <Btn variant="ghost" mutating={false} onClick={onClose}>Annuler</Btn>
        <span style={{ flex: 1 }} />
        <Btn variant="ghost" icon="plus" onClick={onCreate}>Créer un nouveau mapping</Btn>
      </>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {candidates.length === 0 && <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>Aucun mapping existant ne produit {target.type} — créez-en un.</div>}
        {candidates.map((m) => (
          <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '10px 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-800)' }}>{m.name}</div>
              <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>{flowLabel(m)} · v{m.currentVersion}</div>
              <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{m.suppliers.length ? `déjà utilisé par ${m.suppliers.map((s) => s.name).join(', ')}` : 'utilisé par aucun fournisseur'}</div>
            </div>
            <Btn size="sm" icon="check" disabled={busy} onClick={() => void attach(m)}>
              <span aria-label={`Rattacher ${m.name} à ${target.supplierName}`}>Rattacher</span>
            </Btn>
          </div>
        ))}
      </div>
    </Modal>
  );
}

// Extrait les champs canoniques PRODUITS par un mapping (header + lignes + sous-lignes), tous formats confondus.
function mappedFields(rules: unknown): string[] {
  const r = rules as { header?: Array<{ canonical?: string }>; lines?: { fields?: Array<{ canonical?: string }>; sub?: { fields?: Array<{ canonical?: string }> } } };
  const out: string[] = [];
  for (const h of r?.header ?? []) if (h?.canonical) out.push(h.canonical);
  for (const f of r?.lines?.fields ?? []) if (f?.canonical) out.push(f.canonical);
  for (const f of r?.lines?.sub?.fields ?? []) if (f?.canonical) out.push(f.canonical);
  return out;
}

/** Un nom de fichier sûr tiré du nom du mapping — le téléchargement sert à corriger la version hors de la console. */
function fileName(name: string, version: number): string {
  const base = name.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase();
  return `${base || 'mapping'}-v${version}.json`;
}

// Le mapping ouvert : nom, fournisseurs (rattacher / détacher), versions (remettre en service, télécharger),
// champs produits, nouvelle version, suppression quand plus personne ne l'emploie.
function MappingModal({ id, suppliers, onClose, onChanged }: { id: string; suppliers: ApiSupplier[]; onClose: () => void; onChanged: () => Promise<void> | void }) {
  const fieldId = useId();
  const { showToast, guard } = useApp();
  const [d, setD] = useState<ApiMappingDetail | null>(null);
  const [err, setErr] = useState('');
  const [name, setName] = useState('');
  const [addSupplier, setAddSupplier] = useState('');
  const [busy, setBusy] = useState(false);
  const [versionOpen, setVersionOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let live = true;
    (async () => {
      try { const r = await api.get<ApiMappingDetail>(`/admin/mappings/${id}`); if (live) { setD(r); setName(r.name); setErr(''); } }
      catch (e) { if (live) setErr((e as Error).message); }
    })();
    return () => { live = false; };
  }, [id, tick]);

  // Une écriture, puis la relecture du mapping ET de l'écran : ce qui s'affiche vient du serveur, jamais d'une supposition.
  const act = async (fn: () => Promise<unknown>, success: string) => {
    if (!guard()) return;
    setBusy(true);
    try { await fn(); showToast(success, 'ok'); setTick((t) => t + 1); await onChanged(); }
    catch (e) { showToast('Refusé : ' + (e as Error).message, 'err'); }
    finally { setBusy(false); }
  };
  const download = async (version: number) => {
    try {
      const { rules } = await api.get<{ version: number; rules: unknown }>(`/admin/mappings/${id}/versions/${version}`);
      const url = URL.createObjectURL(new Blob([JSON.stringify(rules, null, 2)], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName(d?.name ?? 'mapping', version);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { showToast('Téléchargement impossible : ' + (e as Error).message, 'err'); }
  };

  const fields = d ? mappedFields(d.rules) : [];
  const others = d ? suppliers.filter((s) => !d.suppliers.some((x) => x.id === s.id)) : [];

  return (
    <Modal icon="sparkles" tone="teal" maxWidth={720} title={d?.name ?? 'Mapping'} sub={d ? flowLabel(d) : ''} onClose={onClose}
      footer={d ? <>
        {confirmDelete ? (
          <>
            <span style={{ fontSize: 'var(--t-note)', color: 'var(--rose-fg)' }}>Supprimer « {d.name} » et ses {d.versions.length} version{d.versions.length > 1 ? 's' : ''} ?</span>
            <Btn size="sm" variant="danger" disabled={busy} onClick={() => void act(() => api.del(`/admin/mappings/${id}`), `Mapping « ${d.name} » supprimé`).then(onClose)}>Confirmer</Btn>
            <Btn size="sm" variant="ghost" mutating={false} onClick={() => setConfirmDelete(false)}>Garder</Btn>
          </>
        ) : (
          <Btn size="sm" variant="danger" disabled={busy || d.suppliers.length > 0} onClick={() => setConfirmDelete(true)}>Supprimer</Btn>
        )}
        <span style={{ flex: 1 }} />
        <Btn variant="ghost" mutating={false} onClick={onClose}>Fermer</Btn>
        <Btn icon="upload" disabled={busy} onClick={() => { if (guard()) setVersionOpen(true); }}>Nouvelle version</Btn>
      </> : <Btn mutating={false} onClick={onClose}>Fermer</Btn>}>
      {!d && !err && <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><Spinner size={22} /></div>}
      {err && <div style={{ fontSize: 'var(--t-body)', color: 'var(--rose-fg)' }}>Mapping indisponible : {err}</div>}
      {d && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label htmlFor={`${fieldId}-name`} style={lbl}>Nom</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input id={`${fieldId}-name`} value={name} onChange={(e) => setName(e.target.value)} style={inp} />
              <Btn variant="ghost" disabled={busy || !name.trim() || name.trim() === d.name} onClick={() => void act(() => api.patch(`/admin/mappings/${id}`, { name }), 'Mapping renommé')}>Renommer</Btn>
            </div>
          </div>

          <div>
            <div style={section}>Fournisseurs qu'il traduit ({d.suppliers.length})</div>
            {d.suppliers.length === 0 && <div style={{ fontSize: 'var(--t-note)', color: 'var(--amber-fg)', marginBottom: 6 }}>Aucun — ce mapping ne traduit rien.</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {d.suppliers.map((s) => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)' }}>
                  <Dot kind="ok" />
                  <span style={{ flex: 1, fontSize: 'var(--t-body)', color: 'var(--ink-800)' }}>{s.name}</span>
                  <Btn size="sm" variant="ghost" disabled={busy} onClick={() => void act(() => api.del(`/admin/mappings/${id}/suppliers/${s.id}`), `${s.name} détaché — ses fichiers ${d.canonicalType} ne seront plus traduits par ce mapping`)}>
                    <span aria-label={`Détacher ${s.name}`}>Détacher</span>
                  </Btn>
                </div>
              ))}
            </div>
            {others.length > 0 && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <select aria-label="Fournisseur à rattacher" value={addSupplier} onChange={(e) => setAddSupplier(e.target.value)} style={{ ...selSt, height: 34, flex: 1 }}>
                  <option value="">— choisir un fournisseur —</option>
                  {others.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <Btn size="sm" icon="plus" disabled={busy || !addSupplier} onClick={() => { const sid = addSupplier; void act(() => api.put(`/admin/mappings/${id}/suppliers/${sid}`), 'Fournisseur rattaché').then(() => setAddSupplier('')); }}>Rattacher</Btn>
              </div>
            )}
          </div>

          <div>
            <div style={section}>Versions</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {d.versions.map((v) => (
                <div key={v.version} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 'var(--t-note)', color: 'var(--ink-800)', width: 34 }}>v{v.version}</span>
                  <span style={{ flex: 1, fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{dateTime(v.createdAt)}</span>
                  {v.version === d.currentVersion
                    ? <Badge kind="ok">en service</Badge>
                    : (
                      <Btn size="sm" variant="ghost" disabled={busy} onClick={() => void act(() => api.put(`/admin/mappings/${id}/current`, { version: v.version }), `Version ${v.version} remise en service`)}>
                        <span aria-label={`Remettre en service la version ${v.version}`}>Remettre en service</span>
                      </Btn>
                    )}
                  <Btn size="sm" variant="ghost" mutating={false} icon="download" onClick={() => void download(v.version)}>
                    <span aria-label={`Télécharger la version ${v.version}`}>JSON</span>
                  </Btn>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div style={section}>Champs produits par la version en service ({fields.length})</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {fields.length === 0 && <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-400)' }}>aucun champ (mapping vide ?)</span>}
              {fields.map((f) => <span key={f} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-700)', background: 'var(--ink-100)', padding: '3px 8px', borderRadius: 'var(--r-xs)' }}>{f}</span>)}
            </div>
          </div>

          {d.suppliers.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '10px 12px', borderRadius: 'var(--r-sm)', background: 'var(--blue-bg)', color: 'var(--blue-fg)', fontSize: 'var(--t-dense)', lineHeight: 1.5 }}>
              <Icon name="info" size={15} /><span>Pour le supprimer, détachez-le d'abord de ses fournisseurs : leur traduction s'arrêterait sans un mot.</span>
            </div>
          )}
        </div>
      )}
      {versionOpen && d && <VersionModal mapping={d} onClose={() => setVersionOpen(false)} onDone={async () => { setVersionOpen(false); setTick((t) => t + 1); await onChanged(); }} />}
    </Modal>
  );
}
